import API, { getloginUserId } from "../api/axios";
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import CyclesScroller from "../components/WeeksScroller"; // Updated component name
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import ProjectDetailsModal from "../components/ProjectDetailsModal";
import { Navigate, useNavigate } from "react-router-dom";

dayjs.extend(isoWeek);

// Dummy data for fallback
const dummyProjects = [
  {
    id: "dummy-1",
    name: "Project Alpha",
    tagline: "This is the alpha project.",
    logo_url: "https://picsum.photos/id/1011/100/100",
    votes_count: 12,
    winner_rank: null,
    categories: [{ name: "Category A" }, { name: "Category B" }],
  },
  {
    id: "dummy-2",
    name: "Project Beta",
    tagline: "Beta project tagline.",
    logo_url: "https://picsum.photos/id/1015/100/100",
    votes_count: 5,
    winner_rank: null,
    categories: [{ name: "Category C" }],
  },
  {
    id: "dummy-3",
    name: "Project Gamma",
    tagline: "Gamma project tagline.",
    logo_url: "https://picsum.photos/id/1027/100/100",
    votes_count: 0,
    winner_rank: null,
    categories: [],
  },
];

export default function LeaderBoard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectDetails, setProjectsDetails] = useState();
  const [showModal, setShowModal] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [winnerVotesRequire, SetWinnerVotesRequire] = useState(0);
  const [selectedCycle, setSelectedCycle] = useState(null); 
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/settings/")
      .then((res) => {
        const winner_votes_require = res.data.find(
          (item) => item.key === "winner_votes"
        );
        if (winner_votes_require) {
          SetWinnerVotesRequire(Number(winner_votes_require.value));
        }
      })
      .catch((err) => {
        console.error("Error fetching settings:", err);
      });
  }, []);

  const displayProjects = projects; 
  const hasWinnerRanks = projects.some(p => p.winner_rank);

  const fetchProjects = async (cycle = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cycle?.id) {
        params.append('cycle', cycle.id);
      }
      
      const res = await API.get(`/projects?${params.toString()}`);
      const newProjects = res.data?.data || [];
      
      setProjects(newProjects);
      setApiError(false);
    } catch (err) {
      console.error("Error loading projects:", err);
      setApiError(true);
      
      // Show dummy data on error
      setProjects(dummyProjects);
    } finally {
      setLoading(false);
    }
  };

  const handleCycleChange = (cycle) => {
    if (selectedCycle && cycle.id === selectedCycle.id) return;
    
    setSelectedCycle(cycle);
    fetchProjects(cycle);
  };

  // Load projects when cycle changes
  useEffect(() => {
    if (selectedCycle) {
      fetchProjects(selectedCycle);
    }
  }, [selectedCycle]);

  const handleVote = async (projectId) => {
    if (typeof projectId === "string" && projectId.startsWith("dummy-")) {
      toast.error("This is sample data. Voting is not available.");
      return;
    }

    try {
      const res = await API.post("/votes", {
        user_id: getloginUserId(),
        project_id: projectId,
      });

      const totalVotes = res.data?.data?.total_votes ?? 0;

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, votes_count: totalVotes } : p
        )
      );
    } catch (err) {
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      }
      console.error("Error submitting vote:", err);
    }
  };

  const viewprojectDetails = async (projectID) => {
    if (typeof projectID === "string" && projectID.startsWith("dummy-")) {
      toast.info("Project details are not available for sample data.");
      return;
    }

    try {
      const res = await API.get(`/projects-details/${projectID}`);
      if (res.data && res.data.success) {
        setProjectsDetails(res.data.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error("Category fetch failed:", err);
      toast.error("Failed to load project details.");
    }
  };

  const renderRankDisplay = (project, index, hasWinnerRanks) => {
    if (hasWinnerRanks) {
      if (project.winner_rank === 1) {
        return <img src="./week_medal_1.svg" alt="Gold Medal" />;
      } else if (project.winner_rank === 2) {
        return <img src="./week_medal_2.svg" alt="Silver Medal" />;
      } else if (project.winner_rank === 3) {
        return <img src="./week_medal_3.svg" alt="Bronze Medal" />;
      } else {
        return <strong>#{index + 1}</strong>;
      }
    } else {
      return <strong>#{index + 1}</strong>;
    }
  };

  return (
    <Layout>
      <h2 className="mb-4">Leaderboard</h2>
      <CyclesScroller
        onCycleSelect={handleCycleChange}
        initialSelectedCycle={selectedCycle}
      />

      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading projects...</span>
          </div>
        </div>
      )}

      {!loading && (
        <div className="projects-detail-list d-flex flex-column row">
          {displayProjects.map((project, index) => (
            <div
              className="projects-detail-item"
              key={`${project.id}-${selectedCycle?.id || 'no-cycle'}`}
              data-index={project.id}
              data-item-index={index}
            >
              {/* Logo Column */}
              <div className="project-img">
                {selectedCycle && selectedCycle.status !== 'active' && (
                  <span className="project-rank">
                    {renderRankDisplay(project, index, hasWinnerRanks)}
                  </span>
                )}
                {project.logo_url ? 
                  <img
                      src={project.logo_url || "./default_project_img.png"}
                      className="pro-img rounded"
                      alt={project.name || "default project image"}
                      onError={(e) => { e.target.src = "./default_project_img.png"; }}
                  />
                  : 
                  <img
                      src={"./default_project_img.png"}
                      className="pro-img rounded"
                      alt={project.name || "default project image"}
                  />
                }
              </div>

              {/* Info Column */}
              <div className="project-content">
                <h6
                  className="mb-1 text-muted fw-semibold small"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => viewprojectDetails(project.id)}
                >
                  {project.name}
                </h6>
                <div className="fw-normal mb-1 small">{project.tagline}</div>
                <div className="d-flex align-items-center text-muted small">
                  <span className="me-3 project-category-list">
                    <FontAwesomeIcon icon={faTag} />{" "}
                    {project.categories.length > 0
                      ? project.categories.map((cat) => cat.name).join(", ")
                      : "No categories"}
                  </span>
                </div>
              </div>

              {/* Vote Button Column */}
              <div className="upvote-icon-wrapper">
                <button
                  className="btn btn-white upvote-btn d-flex flex-column align-items-center"
                  type="button"
                  disabled={selectedCycle && selectedCycle.status !== "active"}
                  onClick={() => {
                    if (!getloginUserId()) {
                      navigate("/login");
                    } else if (selectedCycle && selectedCycle.status === "active") {
                      handleVote(project.id);
                    } else {
                      toast.info("Voting is disabled because this cycle is completed.");
                    }
                  }}
                >
                  <div className="upvote-icons">
                    <FontAwesomeIcon icon={faArrowUp} />
                  </div>
                  <div className="upvote-counts">
                    <span>{project.votes_count}</span>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && projects.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">No projects found for the selected cycle.</p>
        </div>
      )}

      <ProjectDetailsModal
        showModal={showModal}
        setShowModal={setShowModal}
        projectDetails={projectDetails}
      />
    </Layout>
  );
}